import * as React from "react";
import {
  motion,
  useReducedMotion,
  type Variants,
  type TargetAndTransition,
} from "framer-motion";

type Props = {
  className?: string;
  /** start/stop animation; defaults to true so it behaves like before */
  play?: boolean;
  /** bump this to restart the timeline when re-entering view */
  replayKey?: number;
};

export default function A1Hero({
  className = "w-full h-auto",
  play = true,
  replayKey,
}: Props) {
  const prefersReduced = useReducedMotion();
  const animOn = play && !prefersReduced;

  const floatTransition = animOn
    ? { duration: 3, repeat: Infinity, repeatType: "mirror" as const, ease: "easeInOut" as const }
    : { duration: 0 };

  const checkVariant: Variants = {
    hidden: { scale: 0.6, opacity: 0, rotate: -8 },
    visible: (i: number): TargetAndTransition => ({
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: animOn
        ? { delay: 0.2 + i * 0.25, type: "spring", stiffness: 420, damping: 24 }
        : { duration: 0 },
    }),
  };

  return (
    <motion.svg
      key={replayKey}                // <-- remount to replay when requested
      viewBox="0 0 1200 675"
      className={className}
      role="img"
      aria-labelledby="a1-title a1-desc"
    >
      <title id="a1-title">Capture tips: face the light, plain background, hair back</title>
      <desc id="a1-desc">A phone showing a portrait with animated checkmarks.</desc>

      <defs>
        <linearGradient id="bgGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F8F4EE" />
          <stop offset="100%" stopColor="#F8F4EE" />
        </linearGradient>
        <linearGradient id="screenGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F8F4EE" />
          <stop offset="100%" stopColor="#EFE7DC" />
        </linearGradient>
        <linearGradient id="glowGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.20" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.0" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="1200" height="675" fill="url(#bgGrad)" />

      {/* soft blobs */}
      <motion.circle
        cx="1050" cy="140" r="120" fill="url(#glowGrad)"
        animate={animOn ? { opacity: [0.35, 0.8, 0.35] } : undefined}
        transition={animOn ? { duration: 2.4, repeat: Infinity, ease: "easeInOut" } : undefined}
      />
      <motion.circle
        cx="140" cy="560" r="140" fill="url(#glowGrad)" style={{ opacity: 0.25 }}
        animate={animOn ? { opacity: [0.25, 0.6, 0.25] } : undefined}
        transition={animOn ? { duration: 2.6, repeat: Infinity, ease: "easeInOut" } : undefined}
      />

      {/* phone float */}
      <motion.g
        initial={animOn ? { y: 4 } : { y: 0 }}
        animate={animOn ? { y: -6 } : { y: 0 }}
        transition={floatTransition}
      >
        {/* phone & portrait ... (unchanged) */}
        <g transform="translate(210,60)">
          <rect x="0" y="0" rx="36" ry="36" width="420" height="560" fill="#F8F4EE" stroke="rgba(31,36,33,.12)" strokeWidth="2" />
          <rect x="22" y="26" rx="28" ry="28" width="376" height="508" fill="url(#screenGrad)" stroke="rgba(31,36,33,.12)" strokeWidth="1.5" />
          <circle cx="210" cy="40" r="4" fill="rgba(31,36,33,.28)" />
          <rect x="190" y="48" width="40" height="4" rx="2" fill="rgba(31,36,33,.16)" />
          <g>
            <path d="M210,410 C160,410 135,450 120,492 L300,492 C285,448 260,410 210,410 Z" fill="rgba(42,164,166,.18)" opacity="0.65" />
            <circle cx="210" cy="300" r="70" fill="rgba(42,164,166,.28)" />
            <path d="M150,305 C150,255 178,230 210,230 C242,230 270,255 270,305 C265,295 250,288 235,288 C218,288 200,294 190,305 C176,320 158,320 150,305 Z" fill="rgba(42,164,166,.40)" opacity="0.9" />
            <line x1="180" y1="305" x2="195" y2="305" stroke="rgba(42,164,166,.55)" strokeWidth="3" strokeLinecap="round" />
            <line x1="225" y1="305" x2="240" y2="305" stroke="rgba(42,164,166,.55)" strokeWidth="3" strokeLinecap="round" />
            <path d="M195,330 C205,340 215,340 225,330" stroke="rgba(42,164,166,.55)" strokeWidth="3" fill="none" strokeLinecap="round" />
          </g>
        </g>
      </motion.g>

      {/* checkmarks */}
      <g transform="translate(700,170)">
        <CheckRow i={0} label="Face the light" variants={checkVariant} play={animOn} />
        <g transform="translate(0,90)">
          <CheckRow i={1} label="Plain background" variants={checkVariant} play={animOn} />
        </g>
        <g transform="translate(0,180)">
          <CheckRow i={2} label="Hair back • no makeup" variants={checkVariant} play={animOn} />
        </g>
      </g>

      <text x="700" y="135" fill="#1F2421" fontSize="24" fontWeight={600} letterSpacing="0.3px">
        Get the best results
      </text>
      <text x="700" y="155" fill="rgba(31,36,33,.55)" fontSize="14">Quick capture checklist</text>
    </motion.svg>
  );
}

function CheckRow({
  i,
  label,
  variants,
  play,
}: {
  i: number;
  label: string;
  variants: Variants;
  play: boolean;
}) {
  const off = !play;
  return (
    <g>
      <rect x="0" y="-16" width="430" height="56" rx="14" fill="#F8F4EE" stroke="rgba(31,36,33,.12)" />
      <motion.g custom={i} variants={variants} initial="hidden" animate={play ? "visible" : "hidden"}>
        <circle cx="28" cy="12" r="14" fill="currentColor" opacity="0.12" />
        <circle cx="28" cy="12" r="14" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.38" />
        <motion.path
          d="M20 12 L26 18 L36 8"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={off ? { pathLength: 0 } : { pathLength: 0 }}
          animate={off ? { pathLength: 0 } : { pathLength: 1 }}
          transition={off ? {} : { delay: 0.25 + i * 0.25, duration: 0.6, ease: "easeInOut" }}
        />
      </motion.g>
      <text x="56" y="17" fill="#1F2421" fontSize="16" fontWeight={600}>{label}</text>
      {!off && (
        <motion.circle
          cx="28" cy="12" r="22" fill="currentColor" style={{ opacity: 0.08 }}
          animate={{ opacity: [0.04, 0.14, 0.04] }}
          transition={{ duration: 1.8, delay: 0.25 + i * 0.25, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
    </g>
  );
}
